<template>
    <div v-if="model">
        <rkm-area
            no-margin-top
            title="Identificação do paciente"
        >
            <rkm-row>
                <rkm-col>
                    <h4>
                        {{ form.paciente.nome }}
                        <strong v-if="form.paciente.nomeSocial && form.paciente.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                            ({{ form.paciente.nomeSocial }})
                        </strong>

                        <span
                            v-if="form.paciente.responsavelFamiliar"
                            class="ml-1"
                            title="Responsável familiar"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="crown" />
                        </span>

                        <span
                            v-if="isTea"
                            class="ml-1"
                            title="Pessoa com Transtorno do Espectro Autista - TEA"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="infinity" />
                        </span>

                        <span
                            v-if="isPcd"
                            class="ml-1"
                            title="Pessoa com Deficiência - PCD"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="wheelchair" />
                        </span>
                    </h4>
                </rkm-col>
            </rkm-row>

            <rkm-row v-if="form.acompanhamentosSaude && form.acompanhamentosSaude.length > 0">
                <rkm-col>
                    <div class="health-flags">
                        <span class="health-title">
                            Acompanhamentos:
                        </span>

                        <span
                            v-for="(acompanhamento, index) in form.acompanhamentosSaude"
                            :key="index"
                            :class="`health-badge acompanhamento-${acompanhamento.value}`"
                        >
                            {{ acompanhamento.text }}
                        </span>
                    </div>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="4">
                    <p>
                        <strong>Prontuário:</strong>
                        {{ form.paciente.prontuario }}
                    </p>
                    <p>
                        <strong>CPF:</strong>
                        {{ form.paciente.cpf }}
                    </p>
                    <p>
                        <strong>CNS:</strong>
                        {{ form.paciente.cns }}
                    </p>
                    <p>
                        <strong>RG:</strong>
                        {{ form.paciente.rg || '-' }}
                    </p>
                    <p>
                        <strong>Telefone:</strong>
                        <template v-if="form.paciente.telefoneNumero">
                            {{ form.paciente.telefoneNumero | formatPhoneOrCellPhone }}
                        </template>
                        <template v-else>
                            -
                        </template>
                        <rkm-button
                            v-if="verificarPermissao('update-cadastro-paciente')"
                            @click="$refs.ModalTelefone.open(form.paciente)"
                            class="ml-1"
                            color="primary"
                            title="Alterar telefone"
                            v-rkm-tooltip.hover
                            small
                        >
                            <rkm-icon icon="pen" />
                        </rkm-button>
                    </p>
                </rkm-col>

                <rkm-col cols="4">
                    <p>
                        <strong>Data de nascimento:</strong>
                        {{ form.paciente.dataNascimento }}
                    </p>
                    <p>
                        <strong>Idade:</strong>
                        {{ form.paciente.idade }}
                    </p>
                    <p>
                        <strong>Sexo:</strong>
                        {{ form.paciente.sexo || '-' }}
                    </p>
                    <p>
                        <strong>Nome da mãe:</strong>
                        <template v-if="form.paciente.nomeMae">
                            {{ form.paciente.nomeMae }}
                        </template>
                        <template v-else>
                            <i>Desconhecido</i>
                        </template>
                    </p>
                </rkm-col>

                <rkm-col cols="4">
                    <p>
                        <strong>Classificação:</strong>
                        {{ classificacaoDescricao }}
                    </p>
                    <p>
                        <strong>Unidade de referência:</strong>
                        <template v-if="form.paciente.unidadeReferencia">
                            {{ form.paciente.unidadeReferencia }}
                        </template>
                        <template v-else>
                            <i>Não informado no cadastro do paciente</i>
                        </template>
                    </p>
                    <p>
                        <strong>Área e Microárea:</strong>
                        {{ form.paciente.area || 'Não Informada' }} / {{ form.paciente.microarea || 'Não Informada' }}
                    </p>
                    <p>
                        <strong>INE:</strong>
                        {{ form.paciente.ine || 'Não Informado' }}
                    </p>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <p>
                        <strong>Endereço:</strong>
                        {{ form.paciente.endereco || 'Não informado' }}
                    </p>
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <ModalTelefone ref="ModalTelefone" />

        <FormBusca
            :form="form"
            :model="model"
            :paciente-id="id"
            @cleaned="agendamentos = $event"
            @searched="agendamentos = $event"
            ref="FormBusca"
        />

        <rkm-area
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-button
                    @click="$refs.FormBusca.print"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>
            </template>

            <ListBusca
                :additionals="additionals"
                :agendamentos="agendamentos"
                :paciente-id="id"
                @canceled="$refs.FormBusca.search()"
                @deleted="$refs.FormBusca.search()"
                @updated="$refs.FormBusca.search()"
            />
        </rkm-area>
    </div>
</template>

<script>
// Components.
import FormBusca from './components/busca/Form'
import ListBusca from './components/busca/List'
import ModalTelefone from '@/components/ambulatorio/components/paciente/telefone/Modal'

// Enums.
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import classificacao from '@/enums/cadastro/paciente/classificacao'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import deficienciaBase from '@/enums/cadastro/cidadao/deficienciaBase'
import deficiencia from '@/enums/cadastro/cidadao/deficiencia'

// Filters.
import formats from '@/filters/formats'

// Mixins.
import {error, permissao, repository, stringCase} from '@/mixins'

// Others.
import moment from 'moment'

// Vuex.
import {mapActions} from 'vuex'

export default {
    components: {
        FormBusca,
        ListBusca,
        ModalTelefone,
    },
    computed: {
        classificacaoDescricao() {
            const paciente = this.form.paciente

            if (paciente.subclassificacaoId == this.enums.subclassificacao.TEMPORARIO) {
                return 'Temporário'
            }

            if (paciente.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {
                return 'Outro Município'
            }

            return 'Munícipe'
        },
        isTea() {
            const ids = this.form.paciente.deficienciasBaseIds || []

            return ids.includes(this.enums.deficienciaBase.TEA)
                || this.form.paciente.deficienciaId === this.enums.deficiencia.AUTISMO
        },
        isPcd() {
            const ids = this.form.paciente.deficienciasBaseIds || []

            return ids.some(id => id !== this.enums.deficienciaBase.TEA)
                || (!!this.form.paciente.deficienciaId && this.form.paciente.deficienciaId !== this.enums.deficiencia.AUTISMO)
        },
    },
    created() {
        this.repositories = {
            agendamento: this.getConectaSusRepository('ambulatorio.agendamento'),
        }
    },
    data: () => ({
        additionals: null,
        enums: {preferenciaNome, classificacao, subclassificacao, deficienciaBase, deficiencia},
        form: null,
        agendamentos: [],
        model: null,
        others: {moment},
    }),
    filters: {
        formatPhoneOrCellPhone: formats.formatPhoneOrCellPhone,
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            this.repositories.agendamento.create(this.id, this.camelObjectKeysToSnakeRecursive({origem: 'busca'}))
                .then(response => {
                    this.additionals = Object.assign(response.data.additionals)
                    this.agendamentos = Object.assign(response.data.agendamentos)
                    this.form = Object.assign(response.data.form)
                    this.model = Object.assign(response.data.model)

                    if (this.model.periodo.start) {
                        this.model.periodo.start = this.others.moment(this.model.periodo.start, 'DD/MM/YYYY').format('YYYY-MM-DD')
                    }

                    if (this.model.periodo.end) {
                        this.model.periodo.end = this.others.moment(this.model.periodo.end, 'DD/MM/YYYY').format('YYYY-MM-DD')
                    }

                    this.addWatch()

                    this.$emit('loaded', {additionals: this.additionals, form: this.form})
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        addWatch() {
            this.$watch('model', {
                deep: true,
                handler() {
                    this.agendamentos = []
                },
            })
        },
    },
    mixins: [error, permissao, repository, stringCase],
    mounted() {
        this.load()
    },
    props: {
        id: {type: [Number, String], required: true},
    },
}
</script>

<style scoped>
p {
    margin: 0px;
}

.health-flags {
    margin-top: 9px;
    padding-top: 2px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    border-top: 1px solid #e5e7eb;
}

.health-title {
    font-size: 12px;
    color: #6b7280;
    font-weight: 600;
}

.health-badge {
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
}

.acompanhamento-idoso {
    color: #155724;
}

.acompanhamento-gestante {
    color: #ffb6c1;
}

.acompanhamento-hipertenso,
.acompanhamento-diabetico {
    color: #856404;
}

.acompanhamento-saude-mulher {
    color: #b34db2;
}

.acompanhamento-crescimento-desenvolvimento-infantil {
    color: #004085;
}
</style>